import React, { useState, useEffect } from 'react';
import { Alert, Button, Card, Row, Col, Form, Table } from '@/components/ui';
import { Vehicle, VehicleComponent, ComponentType } from '../../../types/Vehicle';
import vehicleComponentService from '../../../services/vehicleComponentService';
import vehicleService from '../../../services/vehicleService';
import { usePermissions } from '../../../hooks/usePermissions';
import { confirmAction } from '@/components/ui';
import { describeActionError } from '../../../utils/loadError';

interface VehicleComponentsSectionProps {
  vehicle: Vehicle;
  onUpdate: (vehicle: Vehicle) => void;
}

const VehicleComponentsSection: React.FC<VehicleComponentsSectionProps> = ({ vehicle, onUpdate }) => {
  const { hasPermission } = usePermissions();
  const canEdit = hasPermission('vehicle:edit');
  const [components, setComponents] = useState<VehicleComponent[]>([]);
  const [loading, setLoading] = useState(true);
  const [isEditing, setIsEditing] = useState(false);
  const [editingComponent, setEditingComponent] = useState<Partial<VehicleComponent> | null>(null);
  const [showForm, setShowForm] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [deleteError, setDeleteError] = useState<string | null>(null);

  useEffect(() => {
    fetchComponents();
  }, [vehicle.id]);

  const fetchComponents = async () => {
    try {
      setLoading(true);
      const data = await vehicleComponentService.getComponents(vehicle.id);
      setComponents(data);
    } catch (error) {
      console.error('Error fetching components:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleAdd = () => {
    setSaveError(null);
    setEditingComponent({ vehicleId: vehicle.id });
    setShowForm(true);
    setIsEditing(false);
  };

  const handleEdit = (component: VehicleComponent) => {
    setSaveError(null);
    setEditingComponent(component);
    setShowForm(true);
    setIsEditing(true);
  };

  const handleDelete = async (id: number) => {
    if (!(await confirmAction({ title: 'Delete Component?', confirmLabel: 'Delete', destructive: true }))) {
      return;
    }

    setDeleteError(null);
    try {
      await vehicleComponentService.deleteComponent(vehicle.id, id);
      await fetchComponents();
      // Refresh vehicle data
      const updatedVehicle = await vehicleService.getVehicleById(vehicle.id);
      onUpdate(updatedVehicle);
    } catch (error) {
      console.error('Error deleting component:', error);
      setDeleteError(describeActionError(error, "Couldn't delete the component. Try again."));
    }
  };

  const handleSave = async () => {
    if (!editingComponent) return;
    setSaveError(null);

    try {
      if (isEditing && editingComponent.id) {
        await vehicleComponentService.updateComponent(
          vehicle.id,
          editingComponent.id,
          editingComponent
        );
      } else {
        await vehicleComponentService.createComponent(vehicle.id, editingComponent);
      }
      await fetchComponents();
      setShowForm(false);
      setEditingComponent(null);
      // Refresh vehicle data
      const updatedVehicle = await vehicleService.getVehicleById(vehicle.id);
      onUpdate(updatedVehicle);
    } catch (error) {
      console.error('Error saving component:', error);
      setSaveError(describeActionError(error, "Couldn't save the component. Try again."));
    }
  };

  const handleCancel = () => {
    setShowForm(false);
    setEditingComponent(null);
    setSaveError(null);
  };

  if (loading) {
    return <div>Loading components...</div>;
  }

  return (
    <div>
      <div className="tw-flex tw-justify-between tw-items-center tw-mb-4">
        <h2>Vehicle Components</h2>
        {canEdit && !showForm && (
          <Button variant="primary" onClick={handleAdd}>
            <i className="bi bi-plus-lg tw-mr-1"></i>Add Component
          </Button>
        )}
      </div>

      {showForm && (
        <Card className="tw-mb-4">
          <Card.Header>
            <h5>{isEditing ? 'Edit Component' : 'Add Component'}</h5>
          </Card.Header>
          <Card.Body>
            {saveError && (
              <Alert variant="danger" role="alert" className="tw-mb-3">
                {saveError}
              </Alert>
            )}
            <Row>
              <Col md={6}>
                <div className="tw-mb-3">
                  <Form.Label>Component Type</Form.Label>
                  <Form.Select
                    value={editingComponent?.componentType || ''}
                    onChange={(e) => setEditingComponent({ ...editingComponent, componentType: e.target.value as ComponentType || undefined })}
                  >
                    <option value="">Select...</option>
                    <option value={ComponentType.Engine}>Engine</option>
                    <option value={ComponentType.Transmission}>Transmission</option>
                    <option value={ComponentType.Differentials}>Differentials</option>
                    <option value={ComponentType.HydraulicPump}>Hydraulic Pump</option>
                  </Form.Select>
                </div>
              </Col>
              <Col md={6}>
                <div className="tw-mb-3">
                  <Form.Label>Serial Number</Form.Label>
                  <Form.Control
                    type="text"
                    value={editingComponent?.serialNumber || ''}
                    onChange={(e) => setEditingComponent({ ...editingComponent, serialNumber: e.target.value })}
                  />
                </div>
              </Col>
            </Row>
            <div className="tw-flex tw-justify-end">
              <Button variant="success" className="tw-mr-2" onClick={handleSave}>
                Save
              </Button>
              <Button variant="secondary" onClick={handleCancel}>
                Cancel
              </Button>
            </div>
          </Card.Body>
        </Card>
      )}

      {deleteError && (
        <Alert variant="danger" role="alert" dismissible onClose={() => setDeleteError(null)} className="tw-mb-3">
          {deleteError}
        </Alert>
      )}

      {components.length === 0 ? (
        <p>No components found for this vehicle.</p>
      ) : (
        <Table striped responsive>
          <thead>
            <tr>
              <th>Component Type</th>
              <th>Serial Number</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {components.map((component) => (
              <tr key={component.id}>
                <td>{component.componentType || '-'}</td>
                <td>{component.serialNumber || '-'}</td>
                <td>
                  {canEdit && (
                    <>
                      <Button
                        size="sm"
                        variant="primary"
                        className="tw-mr-2"
                        onClick={() => handleEdit(component)}
                      >
                        <i className="bi bi-pencil"></i>
                      </Button>
                      <Button
                        size="sm"
                        variant="danger"
                        onClick={() => handleDelete(component.id)}
                      >
                        <i className="bi bi-trash"></i>
                      </Button>
                    </>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </Table>
      )}
    </div>
  );
};

export default VehicleComponentsSection;
